import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { useBundle } from '@amzn/react-arb-tools';
import { matchPath, useSearchParams } from 'react-router';
import { ReportActionActionName, ReportActionSource } from '@amzn/handshake-painter-typescript-client/v1';
import { CartAddSource } from '@amzn/handshake-painter-typescript-client/v1';
import Row from '@amzn/meridian/row';
import Loader from '@amzn/meridian/loader';
import { RioText, SectionDivider } from 'src/common/styled';
import { RegularButton } from '../Buttons';
import { useAddToCart, useAppInit, useReportAction } from 'src/hooks';
import { sendAuthMessage } from 'src/utils/messages';
import { AuthStatus } from 'src/utils/auth';
import { useAnalyticsContext } from 'src/common/metrics/AnalyticsContext';
import { useBagStore, useCreatorAttributionStore } from 'src/store';
import { routes } from 'src/utils/routes';
import { createAddToCartRequestFromBag, createAddToCartRequestFromProduct } from './utils';
export const AddToCartButton = ({ isDisabled, quantity, isPrime, product }) => {
    const [bundle, isBundleLoading] = useBundle('pages.app');
    const [searchParams, setSearchParams] = useSearchParams();
    const rumClient = useAnalyticsContext();
    const { listBag } = useBagStore();
    const { getCreatorAttributionToken } = useCreatorAttributionStore();
    const bagContents = listBag();
    const { data: appData } = useAppInit();
    const isUserLinked = !!(appData === null || appData === void 0 ? void 0 : appData.linked);
    const { mutate: reportAction } = useReportAction();
    const { mutate: addToCart, isPending } = useAddToCart();
    const onAddToCartClick = (addToCartAfterAuth = false) => {
        const isPOP = matchPath(routes.previewOrderPage, window.location.pathname);
        const cartAddSource = isPOP ? CartAddSource.PreviewPage : CartAddSource.DetailPage;
        const reportActionSource = isPOP ? ReportActionSource.PreviewOrderPage : ReportActionSource.ProductDetailPage;
        // TODO: Emit a metric when the getCreatorAttributionToken returns an empty string.
        const creatorAttributionToken = getCreatorAttributionToken();
        let addToCartRequest;
        if (product) {
            addToCartRequest = createAddToCartRequestFromProduct(product, quantity, creatorAttributionToken);
        }
        else if (isPOP) {
            addToCartRequest = createAddToCartRequestFromBag(bagContents, creatorAttributionToken);
        }
        if (!addToCartRequest)
            return;
        // Do not recapture add to cart reportAction after authentication
        if (!addToCartAfterAuth) {
            reportAction({
                actionName: ReportActionActionName.AddToCartButtonClick,
                source: reportActionSource,
            });
        }
        // If the user is linked, add to cart directly, otherwise trigger authentication with redirect to current page with saveToCart flag
        if (isUserLinked) {
            addToCart({ addToCartRequest, cartAddSource, isPrime });
        }
        else {
            const currentParams = new URLSearchParams(searchParams);
            currentParams.set('saveToCart', 'true');
            const redirectUrl = `${window.location.pathname}?${currentParams.toString()}`;
            sendAuthMessage(rumClient, AuthStatus.AUTHENTICATE, redirectUrl, ReportActionSource.AddToCartButton);
        }
    };
    // Handle saveToCart parameter on page load
    React.useEffect(() => {
        const saveToCartParam = searchParams.get('saveToCart') === 'true';
        if (saveToCartParam && isUserLinked) {
            // Remove saveToCart from URL
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('saveToCart');
            setSearchParams(newParams, { replace: true });
            onAddToCartClick(true);
        }
    }, [searchParams, isUserLinked]);
    if (isBundleLoading)
        return null;
    return (_jsxs(_Fragment, { children: [_jsxs(Row, { "data-testid": "cta-block-divider", width: "100%", widths: ['fill', 'fit', 'fill'], alignmentHorizontal: "center", spacing: "300", children: [_jsx(SectionDivider, { role: "separator" }), _jsx(RioText, { rioColor: "secondary", type: "b100", children: bundle.getMessage('orSaveForLater') }), _jsx(SectionDivider, { role: "separator" })] }), _jsx(RegularButton, { disabled: isDisabled || isPending, onClick: onAddToCartClick, "data-testid": "add-to-cart-button", children: isPending ? (_jsx(Loader, { "data-testid": "add-to-cart-button-pending", size: "small" })) : (bundle.getMessage('saveToAmazonCart')) })] }));
};
